/**
 * Auto-Test Tool Page
 *
 * Execute automatic tests to validate devices during production.
 * Tool color: blue
 *
 * Workflow:
 * 1. Select technology
 * 2. Manage serial ports and detect devices
 * 3. Run auto-test and view results
 */

import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ExternalLink,
  Loader2,
  Play,
  XCircle,
} from 'lucide-react';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router';
import { type ITechnology, technologyApi } from '@/api/production/technologies';
import { ConsoleDisplay } from '@/components/production/ConsoleDisplay';
import { TechnologySearchForm } from '@/components/production/TechnologySearchForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAutoTest } from '@/hooks/production/useAutoTest';
import { cn } from '@/lib/utils';
import type { IAutoTestLog } from '@/types/production/auto-test-log';

// ============================================
// Types
// ============================================

// ============================================
// Status Badge Component
// ============================================

function DeviceStatusBadge({
  status,
}: {
  status: 'fully_identified' | 'partially_identified' | 'not_identified';
}) {
  const config = {
    fully_identified: {
      label: 'Identificado',
      className: 'bg-green-100 text-green-800',
      dotClass: 'bg-green-500',
    },
    partially_identified: {
      label: 'Parcialmente Identificado',
      className: 'bg-yellow-100 text-yellow-800',
      dotClass: 'bg-yellow-500',
    },
    not_identified: {
      label: 'Não Identificado',
      className: 'bg-red-100 text-red-800',
      dotClass: 'bg-red-500',
    },
  };

  const { label, className, dotClass } = config[status];

  return (
    <div className="flex items-center gap-2">
      <div className={cn('h-2 w-2 rounded-full', dotClass)} />
      <span className={cn('text-xs font-medium', className)}>{label}</span>
    </div>
  );
}

// ============================================
// Detected Devices Table
// ============================================
//
function DetectedDevicesTable({
  devices,
}: {
  devices: Array<{ id: string; port: any; equipment?: any; status: any }>;
}) {
  if (devices.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
        <AlertCircle className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Nenhum dispositivo detectado</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Conecte uma porta serial e aguarde a detecção automática
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Serial</TableHead>
            <TableHead>IMEI</TableHead>
            <TableHead>ICCID</TableHead>
            <TableHead>Firmware</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((device) => (
            <TableRow key={device.id}>
              <TableCell>
                <DeviceStatusBadge status={device.status} />
              </TableCell>
              <TableCell className="font-mono text-sm">
                {device.equipment?.serial ?? '--'}
              </TableCell>
              <TableCell className="font-mono text-sm">{device.equipment?.imei ?? '--'}</TableCell>
              <TableCell className="font-mono text-sm">{device.equipment?.iccid ?? '--'}</TableCell>
              <TableCell className="font-mono text-sm">
                {device.equipment?.firmware ?? '--'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ============================================
// Auto Test Results Table
// ============================================

function AutoTestResultsTable({ results }: { results: IAutoTestLog[] }) {
  // Linhas expandidas (dropdown com o detalhe de cada verificação do teste)
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (results.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
        <CheckCircle className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Nenhum teste realizado ainda</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Execute o auto teste para ver os resultados aqui
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Resultado</TableHead>
            <TableHead>Serial</TableHead>
            <TableHead>Firmware</TableHead>
            <TableHead>Data</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {results.map((result) => {
            const isExpanded = expandedIds.has(result.id);
            const analysisEntries = Object.entries(result.analysis || {});

            return (
              <React.Fragment key={result.id}>
                <TableRow
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleExpanded(result.id)}
                  title="Clique para ver o detalhe das verificações"
                >
                  <TableCell>
                    {result.status ? (
                      <Badge variant="success-subtle" className="gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Aprovado
                      </Badge>
                    ) : (
                      <Badge variant="destructive-subtle" className="gap-1">
                        <XCircle className="h-3 w-3" />
                        Reprovado
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {result.equipment?.serial ?? '--'}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {result.equipment?.firmware ?? '--'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(result.created_at).toLocaleString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 text-muted-foreground transition-transform',
                        isExpanded && 'rotate-180',
                      )}
                    />
                  </TableCell>
                </TableRow>

                {isExpanded && (
                  <TableRow className="bg-gray-50/50 hover:bg-gray-50/50">
                    <TableCell colSpan={5} className="py-4">
                      <div className="space-y-4">
                        {analysisEntries.length > 0 ? (
                          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 sm:grid-cols-3 lg:grid-cols-4">
                            {analysisEntries.map(([key, value]) => (
                              <div key={key} className="flex items-center gap-2 text-sm">
                                {value ? (
                                  <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
                                ) : (
                                  <XCircle className="h-4 w-4 shrink-0 text-red-600" />
                                )}
                                <span className="font-mono">{key}</span>
                                <span
                                  className={cn(
                                    'text-xs font-medium',
                                    value ? 'text-green-700' : 'text-red-700',
                                  )}
                                >
                                  {value ? 'OK' : 'FALHOU'}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            Nenhuma verificação registrada para este teste.
                          </p>
                        )}

                        {(result.equipment?.lora_version || result.equipment?.ble_version) && (
                          <div className="flex flex-wrap gap-x-8 gap-y-1.5 text-sm">
                            {result.equipment.lora_version && (
                              <span>
                                <span className="text-muted-foreground">Firmware LoRa: </span>
                                <span className="font-mono">{result.equipment.lora_version}</span>
                              </span>
                            )}
                            {result.equipment.ble_version && (
                              <span>
                                <span className="text-muted-foreground">Firmware BLE: </span>
                                <span className="font-mono">{result.equipment.ble_version}</span>
                              </span>
                            )}
                          </div>
                        )}

                        <Link
                          to={`/production/logs/auto-test/${result.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-500"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Ver log completo
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ============================================
// Main Page Component
// ============================================

export default function AutoTestPage() {
  const [searchParams] = useSearchParams();
  const technologyId = searchParams.get('technology_id');

  // State
  const [selectedTechnology, setSelectedTechnology] = React.useState<ITechnology | null>(null);
  const [consoleLogs, setConsoleLogs] = React.useState<string[]>([]);

  // Fetch technology if ID is in URL
  const { data: technologyData } = useQuery({
    queryKey: ['technology', technologyId],
    queryFn: () => technologyApi.findOne(technologyId!),
    enabled: !!technologyId,
  });

  // Set technology from URL param
  React.useEffect(() => {
    if (technologyData) {
      setSelectedTechnology(technologyData);
    }
  }, [technologyData]);

  // Add log helper
  const addLog = React.useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString('pt-BR');
    const logMessage = `[${timestamp}] ${message}`;
    setConsoleLogs((prev) => [...prev, logMessage]);
    console.log('[AutoTest]', logMessage);
  }, []);

  // Clear logs
  const clearLogs = React.useCallback(() => {
    setConsoleLogs([]);
    console.log('[AutoTest] Logs cleared');
  }, []);

  // Handle technology change
  const handleTechnologyChange = React.useCallback(
    (tech: ITechnology | null) => {
      setSelectedTechnology(tech);
      clearLogs();
      if (tech) {
        addLog(`Tecnologia selecionada: ${tech.name.brand}`);
      }
    },
    [addLog, clearLogs],
  );

  // Use the auto test hook
  const {
    detected,
    testableCount,
    autoTest,
    test,
    requestPort,
    isTesting,
    isDetecting,
    error,
    discarded,
    discardMessages,
  } = useAutoTest({
    technology: selectedTechnology,
  });

  // Log when devices are detected
  const detectedSerialsRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    const currentSerials = new Set(
      detected.filter((d) => d.equipment?.serial).map((d) => d.equipment?.serial!),
    );
    const newSerials = Array.from(currentSerials).filter(
      (serial) => !detectedSerialsRef.current.has(serial),
    );

    newSerials.forEach((serial) => {
      addLog(`Dispositivo detectado: ${serial}`);
    });

    // Compara com o conjunto anterior ANTES de sobrescrever a ref — a ordem
    // invertida tornava esta condição inalcançável.
    if (detected.length === 0 && detectedSerialsRef.current.size > 0) {
      addLog('Nenhum dispositivo detectado');
    }

    detectedSerialsRef.current = currentSerials;
  }, [detected, addLog]);

  // Surface auto test failures and discarded results in the console
  React.useEffect(() => {
    if (error) addLog(`⚠ ${error}`);
  }, [error, addLog]);

  React.useEffect(() => {
    discarded.forEach((item) => {
      const label = item.serial ?? 'dispositivo não identificado';
      addLog(`⚠ ${label} não registrado: ${discardMessages[item.reason]}`);
    });
  }, [discarded, discardMessages, addLog]);

  // Log when auto test completes
  const lastAutoTestLengthRef = React.useRef(0);
  React.useEffect(() => {
    const newCount = autoTest.length - lastAutoTestLengthRef.current;
    if (newCount > 0) {
      const newTests = autoTest.slice(-newCount);
      newTests.forEach((testResult) => {
        const analysisEntries = Object.entries(testResult.analysis || {});
        const passedCount = analysisEntries.filter(([, value]) => value).length;
        const total = analysisEntries.length;

        // Cabeçalho do resultado com resumo de verificações (mesmo padrão do configurator)
        const serial = testResult.equipment?.serial ?? '--';
        if (testResult.status) {
          addLog(`✓ ${serial} aprovado no teste (${passedCount}/${total} OK)`);
        } else {
          const failedCount = total - passedCount;
          addLog(
            `✗ ${serial} reprovado no teste (${passedCount}/${total} OK, ${failedCount} falha(s))`,
          );
        }

        // Mostra TODAS as verificações — passou e falhou — para o usuário ter contexto completo.
        analysisEntries.forEach(([key, value]) => {
          addLog(`  ${value ? '✓' : '✗'} ${key}: ${value ? 'OK' : 'FALHOU'}`);
        });
      });
    }
    lastAutoTestLengthRef.current = autoTest.length;
  }, [autoTest, addLog]);

  // Run auto test with logging
  const handleTest = React.useCallback(async () => {
    addLog('Iniciando auto teste...');
    addLog(`Testando ${testableCount} dispositivo(s)`);
    await test();
  }, [test, testableCount, addLog]);

  return (
    <div className="container mx-auto pb-8 px-4 space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-base font-semibold leading-7 text-gray-900">Auto Test</h1>
        <p className="mt-2 text-sm text-gray-700">
          Execute testes automáticos para validar dispositivos durante o processo de produção.
        </p>
      </div>

      {/* Step 1: Technology Selection */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge
              variant="default"
              className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white"
            >
              1
            </Badge>
            <CardTitle className="text-xl">Definição da tecnologia</CardTitle>
          </div>
          <CardDescription className="ml-11">
            Escolha a tecnologia para o auto teste
          </CardDescription>
        </CardHeader>
        <CardContent className="ml-11">
          <TechnologySearchForm
            technology={selectedTechnology}
            showConfigurationProfile={false}
            showSerialNumber={false}
            onTechnologyChange={handleTechnologyChange}
          />
        </CardContent>
      </Card>

      {/* Step 2: Port Management */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge
              variant={selectedTechnology ? 'default' : 'secondary'}
              className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white"
            >
              2
            </Badge>
            <CardTitle className="text-xl">Gerenciamento de Portas</CardTitle>
            {isDetecting && <Loader2 className="h-4 w-4 animate-spin text-black" />}
          </div>
          <CardDescription className="ml-11">
            Uma lista de todas as portas conectadas vinculadas ao equipamento identificado
          </CardDescription>
        </CardHeader>
        <CardContent className="ml-11 space-y-6">
          {selectedTechnology ? (
            <>
              <DetectedDevicesTable devices={detected} />

              <div className="flex justify-between gap-4">
                {/* Não depende de isDetecting: com vários equipamentos sempre há alguma porta
                    sendo sondada. O test() já espera a detecção em andamento terminar. */}
                <Button
                  onClick={handleTest}
                  disabled={!selectedTechnology || testableCount === 0 || isTesting}
                  className="bg-black hover:bg-gray-800 text-white"
                >
                  {isTesting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Testando...
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Auto Test
                    </>
                  )}
                </Button>

                <Button variant="outline" onClick={requestPort}>
                  Nova Porta
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                Nenhuma tecnologia selecionada
              </h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Complete a etapa anterior para gerenciar as portas seriais.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 3: Results */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge
              variant={autoTest.length > 0 ? 'default' : 'secondary'}
              className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white"
            >
              3
            </Badge>
            <CardTitle className="text-xl">Verificação e Resultados</CardTitle>
            {isTesting && <Loader2 className="h-4 w-4 animate-spin text-black" />}
          </div>
          <CardDescription className="ml-11">
            {autoTest.length > 0
              ? `${autoTest.length} teste(s) realizado(s)`
              : 'Aguardando execução do auto teste'}
          </CardDescription>
        </CardHeader>
        <CardContent className="ml-11 space-y-6">
          {error && (
            <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-4">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-red-800">{error}</p>
                {discarded.length > 0 && (
                  <ul className="list-inside list-disc text-xs text-red-700">
                    {discarded.map((item) => (
                      <li key={item.serial ?? `${item.reason}-${discarded.indexOf(item)}`}>
                        {item.serial ?? 'Dispositivo não identificado'}:{' '}
                        {discardMessages[item.reason]}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <AutoTestResultsTable results={autoTest} />

          {/* Console Output */}
          <ConsoleDisplay
            logs={consoleLogs}
            height={200}
            title="Log de Execução"
            textColor="green"
            bgColor="gray"
            showLineNumbers={false}
            onClear={clearLogs}
          />
        </CardContent>
      </Card>
    </div>
  );
}
//
export { AutoTestPage };
